import { InjectionToken, Signal } from '@angular/core';
import { NavBadge } from './nav-descriptor';

/**
 * A resolved sub-navigation item — the projection of a descriptor's
 * `children` carried alongside its parent {@link NavItem}. Surfaces
 * (the sidebar) render these as the group's sub-items. Keeps the
 * descriptor's `id` so the surface can re-resolve `descriptor.security`
 * for its own per-item `*authGuard` gate.
 */
export interface NavSubItem {
  id: string;
  label: string;
  /** Resolved router path or external URL. */
  target: string;
}

/**
 * A source-agnostic, resolved navigation item.
 *
 * Unlike a {@link NavDescriptor} (a static declaration), a `NavItem`
 * is the live, personalised projection that a surface renders. It
 * carries provenance via `source` so personalised lists (pinned /
 * recent) can be distinguished from the registry-derived defaults.
 */
export interface NavItem {
  id: string;
  label: string;
  icon?: string;
  /** Resolved router path or external URL. */
  target: string;
  badge?: NavBadge;
  /**
   * Explicit ordering hint propagated from the owning
   * {@link NavDescriptor.order}. Drives both the item's position within
   * its group AND (via the group's minimum) the section ordering, so
   * surfaces render the intended sequence rather than collapsing to
   * alphabetical. `undefined` sinks to the end.
   */
  order?: number;
  /**
   * Resolved sub-items projected from the owning descriptor's
   * `children` (sidebar-surface children only, ordered). A group
   * descriptor (Products, Metadata, …) carries its sub-items here so the
   * surface can render the expandable sub-navigation. Empty for leaves.
   */
  subItems?: NavSubItem[];
  /** Where this item originated. */
  source: 'registry' | 'pinned' | 'recent';
  /** Security gate — surfaces hide the item when this returns false. */
  canAccess(): boolean;
}

/**
 * A source of {@link NavItem}s for a section. Implementations may
 * resolve asynchronously; `items()` returns a Signal so surfaces can
 * react to personalisation changes.
 *
 * Phase 1 ships the contract only — no PinnedNavSource / RecentNavSource
 * implementations exist yet.
 */
export interface NavSource {
  /** Optional section grouping label this source contributes to. */
  readonly section?: string;
  items(): Signal<NavItem[]>;
}

/**
 * Multi-provider token for {@link NavSource} implementations.
 */
export const NAV_SOURCE = new InjectionToken<NavSource>('NAV_SOURCE');
